import React from 'react';
import type { DialogueResponse, DialogueV2Receipt } from '@/api/reqventos-dialogue-lab';
import { useT } from '@/i18n';
import { Disclosure } from './common';
import styles from './workspace.module.css';
type Entry = DialogueV2Receipt['results'][number];
/** Display data is resolved by the tenant-scoped API from this run's frozen bytes, never today's rules. */
export function RuleFindings({ entry, sources }: { entry: Entry; sources: DialogueResponse<'detail'>['rule_sources'] }) {
  const t = useT(), result = entry.result;
  const resolved = sources?.state === 'available' ? sources.results.find(r => r.step_id === entry.step_id) : null;
  const facts = [...result.facts, ...('business_facts' in result ? result.business_facts : [])];
  const used = new Set(result.rules.flatMap(rule => rule.fact_ids));
  const factAnchor = (id: string) => `rule-fact-${entry.step_id}-${facts.findIndex(f => f.id === id)}`;
  return <Disclosure title={t('dialogueLab.rules')}>
    {!result.rules.length && <p>{t('dialogueLab.rules.empty')}</p>}
    {result.schema_version === '2.3.0' && <p className={styles.muted}>{t('dialogueLab.rules.derived')}</p>}
    {result.rules.map((rule, index) => {
      const compact = 'rule_id' in rule;
      const source = compact ? resolved?.rules[index] : null;
      const matching = compact && source?.rule_id === rule.rule_id ? source : null;
      return <div className={styles.finding} key={index}>
        <strong>{compact ? matching?.title ?? rule.rule_id : rule.policy_section}</strong>
        {compact && <p className={styles.mono}>{rule.rule_id}</p>}
        <p>{rule.conclusion}</p>
        {compact ? matching ? matching.clauses.map(clause => <Disclosure key={clause.id} title={`${clause.id} · ${clause.section}`}>
          <blockquote className={styles.prose}>{clause.text}</blockquote>
        </Disclosure>) : <p role="status">{t('dialogueLab.rules.unavailable')}</p> : <blockquote className={styles.prose}>{rule.policy_quote}</blockquote>}
        <p>{t('dialogueLab.rules.facts')}: {rule.fact_ids.length ? rule.fact_ids.map((id, i) => <React.Fragment key={id}>
          {i > 0 && ', '}{facts.some(f => f.id === id) ? <a href={`#${factAnchor(id)}`}>{id}</a> : id}
        </React.Fragment>) : t('dialogueLab.rules.noFacts')}</p>
      </div>;
    })}
    {facts.filter(fact => used.has(fact.id)).map(fact => <div className={styles.finding} id={factAnchor(fact.id)} key={fact.id} tabIndex={-1}>
      <strong>{fact.id} · {'field' in fact ? fact.field : fact.source_id}</strong>
      <p>{typeof fact.value === 'string' ? fact.value : JSON.stringify(fact.value)}</p>
      {'evidence' in fact && fact.evidence.map((evidence, i) => <p key={i}><code>{evidence.source_id}</code> · <q>{evidence.quote}</q></p>)}
    </div>)}
  </Disclosure>;
}
